// Local worksheet library.
//
// Saves each finished worksheet to IndexedDB so the user can find it again. Stores the
// canvas-rendered PNG and the exported PDF as Blobs, plus metadata (title, subject,
// year level, dates).
//
// Why IndexedDB and not localStorage:
//   - localStorage has a tiny quota (~5 MB) and only stores strings.
//   - IndexedDB stores Blobs directly, has ~50 MB+ quota in most browsers.
//   - Survives across sessions, works offline.
//
// Schema versions only ever go up. The upgrade callback adds new object stores or
// indexes; never removes data the user has.

const DB_NAME = 'mdt_worksheets';
const DB_VERSION = 1;
const STORE = 'worksheets';

export interface WorksheetRecord {
  /** Stable identifier. Generated at save time. */
  id: string;
  /** Auto-generated human-friendly title: "Theo - English - Year 4 - 12 May 2026". */
  title: string;
  /** AI-detected subject (Maths, English, Science, etc.). Lower-case canonical form. */
  subject: string;
  /** Display subject (Maths, English). Same value with the canonical casing for the brand. */
  subjectLabel: string;
  /** AI-detected year level if obvious from the worksheet ("Year 4", "GCSE", etc.). */
  yearLevel?: string;
  /** User's name from Profile at save time. Empty if not set. */
  userName?: string;
  /** Epoch ms when the worksheet was captured. */
  capturedAt: number;
  /** Epoch ms when the user exported the worksheet (the "downloaded" time). */
  downloadedAt?: number;
  /** Original captured photo with answers rendered. */
  imageBlob: Blob;
  /** Exported PDF blob. */
  pdfBlob?: Blob;
  questionsCount: number;
  answersCount: number;
}

// Open the database. Creates the worksheets store on first run.
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('subject', 'subject');
        store.createIndex('capturedAt', 'capturedAt');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function runTx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const req = fn(store);
        req.onsuccess = () => resolve(req.result as T);
        req.onerror = () => reject(req.error);
        tx.onerror = () => reject(tx.error);
      })
  );
}

export async function saveWorksheet(record: Omit<WorksheetRecord, 'id'> & { id?: string }): Promise<string> {
  const id = record.id ?? `ws-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const full: WorksheetRecord = { ...record, id };
  await runTx<IDBValidKey>('readwrite', (store) => store.put(full));
  return id;
}

export async function updateWorksheet(id: string, updates: Partial<WorksheetRecord>): Promise<void> {
  const existing = await getWorksheet(id);
  if (!existing) return;
  const merged = { ...existing, ...updates };
  await runTx<IDBValidKey>('readwrite', (store) => store.put(merged));
}

export async function listWorksheets(): Promise<WorksheetRecord[]> {
  try {
    const all = await runTx<WorksheetRecord[]>('readonly', (store) => store.getAll());
    return all.sort((a, b) => b.capturedAt - a.capturedAt);
  } catch {
    return [];
  }
}

export async function getWorksheet(id: string): Promise<WorksheetRecord | undefined> {
  try {
    return await runTx<WorksheetRecord | undefined>('readonly', (store) => store.get(id));
  } catch {
    return undefined;
  }
}

export async function deleteWorksheet(id: string): Promise<void> {
  await runTx<void>('readwrite', (store) => store.delete(id));
}

// ──────────────────────────────────────────────────────────────────────────────
// Title generation
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Build the auto-title for a worksheet using whatever fields are available.
 * Format: "{userName} - {subject} - {yearLevel} - {date}".
 * Missing parts are skipped. Date always present.
 */
export function buildWorksheetTitle(parts: {
  userName?: string;
  subjectLabel: string;
  yearLevel?: string;
  capturedAt: number;
}): string {
  const pieces: string[] = [];
  if (parts.userName) pieces.push(parts.userName);
  if (parts.subjectLabel) pieces.push(parts.subjectLabel);
  if (parts.yearLevel) pieces.push(parts.yearLevel);
  pieces.push(formatDate(parts.capturedAt));
  return pieces.join(' - ');
}

export function formatDate(epochMs: number): string {
  const d = new Date(epochMs);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateTime(epochMs: number): string {
  const d = new Date(epochMs);
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// Grouping for the History screen
// ──────────────────────────────────────────────────────────────────────────────

export interface SubjectGroup {
  subject: string;
  subjectLabel: string;
  months: MonthGroup[];
}

export interface MonthGroup {
  /** "May 2026" */
  monthLabel: string;
  /** Epoch ms of the 1st of the month, used to sort. */
  monthSort: number;
  worksheets: WorksheetRecord[];
}

/**
 * Group worksheets first by subject (only subjects with content), then by month
 * (only months with content). Within a month, worksheets are sorted newest first.
 * Subjects sorted by their most-recent-worksheet date.
 */
export function groupBySubjectAndMonth(records: WorksheetRecord[]): SubjectGroup[] {
  const bySubject = new Map<string, WorksheetRecord[]>();
  for (const r of records) {
    const key = r.subject || 'other';
    const arr = bySubject.get(key) ?? [];
    arr.push(r);
    bySubject.set(key, arr);
  }

  const groups: SubjectGroup[] = [];
  for (const [subject, arr] of bySubject) {
    const byMonth = new Map<string, { label: string; sort: number; arr: WorksheetRecord[] }>();
    for (const r of arr) {
      const d = new Date(r.capturedAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      const sort = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
      const bucket = byMonth.get(key) ?? { label, sort, arr: [] };
      bucket.arr.push(r);
      byMonth.set(key, bucket);
    }
    const months: MonthGroup[] = Array.from(byMonth.values())
      .map((b) => ({
        monthLabel: b.label,
        monthSort: b.sort,
        worksheets: b.arr.sort((a, c) => c.capturedAt - a.capturedAt),
      }))
      .sort((a, b) => b.monthSort - a.monthSort);

    groups.push({
      subject,
      subjectLabel: arr[0].subjectLabel || titleCase(subject),
      months,
    });
  }

  // Sort subjects by their most recent worksheet (newest activity first).
  groups.sort((a, b) => {
    const aMost = Math.max(...a.months[0].worksheets.map((w) => w.capturedAt));
    const bMost = Math.max(...b.months[0].worksheets.map((w) => w.capturedAt));
    return bMost - aMost;
  });

  return groups;
}

function titleCase(s: string): string {
  return s
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}
